import { useState, useEffect } from 'react';
import { onAuthStateChanged, signOut as firebaseSignOut } from 'firebase/auth';
import { auth } from '../lib/firebase';
import { environment } from '../config/environment';
import { getAdminTokenVerificationError } from '../utils/adminMagicLink';
import { getProjectAuthClaims, signInWithProjectToken } from '../lib/projectAuth';

interface AdminUser {
  uid: string;
  email: string;
  isAdmin: boolean;
  lastLogin: string;
  sessionExpires: string;
}

export function useAdminAuth() {
  const [user, setUser] = useState<AdminUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);

  const isIdealssEmail = (email: string): boolean => {
    return email.toLowerCase().endsWith('@idealss.com');
  };

  const sendAdminMagicLink = async (email: string): Promise<void> => {
    if (!isIdealssEmail(email)) {
      throw new Error('Access denied. This email address is not authorized for admin access.');
    }

    try {
      const generateUrl = environment.useEmulators
        ? 'http://127.0.0.1:5001/ima-onboard/us-central1/generateAdminMagicLink'
        : 'https://us-central1-ima-onboard.cloudfunctions.net/generateAdminMagicLink';

      const response = await fetch(generateUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to generate admin magic link');
      }

      console.log('Admin magic link sent to:', email);
    } catch (error) {
      console.error('Error sending admin magic link:', error);
      throw error;
    }
  };

  const verifyAdminMagicLink = async (tokenOverride?: string): Promise<void> => {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const token = tokenOverride || urlParams.get('token');

      if (!token) {
        throw new Error('No token found in URL');
      }

      const verifyUrl = environment.useEmulators
        ? 'http://127.0.0.1:5001/ima-onboard/us-central1/verifyAdminMagicLink'
        : 'https://us-central1-ima-onboard.cloudfunctions.net/verifyAdminMagicLink';

      const response = await fetch(verifyUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token }),
      });

      const result = await response.json();

      const verificationError = getAdminTokenVerificationError(response.ok, result);
      if (verificationError) {
        throw new Error(verificationError);
      }

      if (!result.customToken) {
        throw new Error('Authentication token missing from server response');
      }

      await signInWithProjectToken(result.customToken);

      const adminUser: AdminUser = {
        uid: result.uid,
        email: result.email,
        isAdmin: true,
        lastLogin: new Date().toISOString(),
        sessionExpires: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
      };

      localStorage.setItem('adminUser', JSON.stringify(adminUser));
      setUser(adminUser);
      setSessionExpired(false);

      console.log('Admin user authenticated:', result.email);
    } catch (error) {
      console.error('Error verifying admin magic link:', error);
      throw error;
    }
  };

  const checkSessionValidity = (adminUser: AdminUser): boolean => {
    const now = new Date();
    const sessionExpires = new Date(adminUser.sessionExpires);
    return now < sessionExpires;
  };

  const adminSignOut = async (): Promise<void> => {
    try {
      localStorage.removeItem('adminUser');
      await firebaseSignOut(auth);
      setUser(null);
      setSessionExpired(false);
      console.log('Admin user signed out');
    } catch (error) {
      console.error('Error signing out admin:', error);
      throw error;
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setLoading(true);

      try {
        if (!firebaseUser) {
          localStorage.removeItem('adminUser');
          setUser(null);
          setSessionExpired(false);
          setLoading(false);
          return;
        }

        const claims = await getProjectAuthClaims(firebaseUser);
        if (!claims || claims.role !== 'admin' || !claims.email || !isIdealssEmail(claims.email)) {
          localStorage.removeItem('adminUser');
          setUser(null);
          setSessionExpired(true);
          setLoading(false);
          return;
        }

        const storedAdmin = localStorage.getItem('adminUser');
        const adminUser: AdminUser = storedAdmin
          ? (JSON.parse(storedAdmin) as AdminUser)
          : {
              uid: firebaseUser.uid,
              email: claims.email,
              isAdmin: true,
              lastLogin: new Date().toISOString(),
              sessionExpires: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
            };

        if (!checkSessionValidity(adminUser)) {
          localStorage.removeItem('adminUser');
          await firebaseSignOut(auth);
          setUser(null);
          setSessionExpired(true);
          setLoading(false);
          return;
        }

        setUser(adminUser);
        setSessionExpired(false);
      } catch (error) {
        console.error('Error checking admin session:', error);
        localStorage.removeItem('adminUser');
        setUser(null);
        setSessionExpired(true);
      }

      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  return {
    user,
    loading,
    sessionExpired,
    sendAdminMagicLink,
    verifyAdminMagicLink,
    adminSignOut,
    isIdealssEmail,
  };
}
